import gql from 'graphql-tag';
import React from 'react';
import ReactPlayer from 'react-player';
import { batch, useDispatch, useSelector } from 'react-redux';

import {
  setActiveWordInTranscribe,
  setPlayerDuration,
  setPlayingProgress,
  setPlayingState,
  setSeekFunction,
} from '../../actions';
import {
  TranscriptItemInput,
  UpdateRecordingInput,
  UpdateRecordingMutation,
} from '../../graphql/api';
import { IState } from '../../graphql/types';
import useMutation from '../../hooks/useMutation';
import useRecording from '../../hooks/useQuery/useRecording';

const updateRecording = gql`
  mutation UpdateRecording($input: UpdateRecordingInput!) {
    updateRecording(input: $input) {
      id
      owner
      tenantID
    }
  }
`;

const SoundElement: React.FC<{ url: string; dbLength: number; id: string }> = ({
  url,
  dbLength,
  id,
}) => {
  const isInLibrary = useSelector<IState, boolean>(
    state =>
      !!(
        state.openedStepOrder &&
        state.steps?.[state.openedStepOrder]?.isInLibrary
      )
  );
  const dispatch = useDispatch();
  const playerRef = React.createRef<ReactPlayer>();
  const playing = useSelector<IState, boolean>(
    state => !!state.player?.playing
  );
  const { callMutation } = useMutation<
    UpdateRecordingInput,
    UpdateRecordingMutation
  >(updateRecording);
  const { recording } = useRecording(id);
  const time = useSelector<IState, number>(
    state => state.player?.progressTime ?? 0
  );
  const activeWordInTranscribe = useSelector<
    IState,
    TranscriptItemInput | undefined
  >(state => state.activeWordInTranscribe);

  const onProgress = React.useCallback(
    ({ playedSeconds }) => {
      if (playing) {
        const feedCards = recording?.feedCards?.items;
        if (feedCards?.length) {
          feedCards.forEach(card => {
            if (
              card?.id &&
              card?.time &&
              Math.abs(card?.time - Number(playedSeconds.toFixed(2))) < 0.02
            ) {
              document.getElementById(card?.id)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            }
          });
        }

        const words = recording?.transcriptResults?.items;
        if (words?.length) {
          words.forEach((word: TranscriptItemInput) => {
            if (
              word.start_time &&
              word.end_time &&
              playedSeconds >= word.start_time &&
              playedSeconds <= word.end_time
            ) {
              if (activeWordInTranscribe !== word) {
                dispatch(setActiveWordInTranscribe(word));
              }
            }
          });
        }
        dispatch(setPlayingProgress(playedSeconds * 1000));
      }
    },
    [dispatch, recording, playing, activeWordInTranscribe]
  );

  React.useEffect(() => {
    if (playerRef.current?.seekTo) {
      dispatch(setSeekFunction(playerRef.current.seekTo));
    }
  }, [playerRef, dispatch]);

  return (
    <ReactPlayer
      ref={playerRef}
      url={url}
      playing={playing}
      progressInterval={20}
      onEnded={() => {
        playerRef.current?.seekTo(0);
        batch(() => {
          dispatch(setPlayingState(false));
          dispatch(setActiveWordInTranscribe(undefined));
        });
      }}
      width={0}
      onReady={() => {
        const actualLength = Math.round(
          (playerRef.current?.getDuration() ?? 0) * 1000
        );
        dispatch(setPlayerDuration(actualLength));
        if (
          !isInLibrary &&
          (dbLength - actualLength > 300 || dbLength - actualLength < -300)
        ) {
          callMutation({
            id,
            length: actualLength,
          });
        }
      }}
      onStart={() => {
        if (time > 0) playerRef.current?.seekTo(time / 1000);
      }}
      height={0}
      onProgress={onProgress}
    />
  );
};

export default SoundElement;
